<template>
  <div class="mobile-view">
    <header class="header">
      <div class="header-content">
        <a class="back" @click.prevent="goBack" role="button" tabindex="0">
          <span class="iconfont-arrow-left"></span>
        </a>
        <div class="heading-primary">{{ $t('shopping.basket.to_checkout') }}</div>
      </div>
      <button ref="closeBtnRef" type="button" class="header-close iconfont-cross" aria-label="Close"></button>
    </header>
    <div class="content checkout" ref="container">
      <ShippingDetails />
      <CartVoucherBlock />
      <FreeSamplesBlock :hasBottomBorder="true" />
      <CartOverview :readonly="true" :isClickActionEnabled="false" />
    </div>
    <footer class="footer">
      <PurchaseSummary @submit="openPaymentOverlay" :isPurchaseStep="true" />
    </footer>
    <BaseModal v-model="isModalOpen" :bottomSheetOnMobile="true" class="payment-modal">
      <template #header>{{ $t('shopping.basket.checkout.payment_method') }}</template>
      <ul class="payment-options" v-if="adyenInstance">
        <li
            v-for="paymentMethod in availablePaymentMethods"
            :key="paymentMethod.service_type"
            :class="{active: activePaymentMethod === paymentMethod.service_type}"
        >
          <div class="payment-option" @click="handlePaymentMethodChange(paymentMethod)">
            <div class="tick">
              <span class="iconfont-check" v-if="activePaymentMethod === paymentMethod.service_type"></span>
            </div>
            <div class="description">
              <img :src="paymentMethod.icon" class="icon" :alt="paymentMethod.name" />
              <span>{{ paymentMethod.name }}</span>
            </div>
          </div>
          <Walley
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'walley'"
          />
          <Klarna
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'klarna'"
          />
          <PayPal
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'paypal'"
          />
          <Trustly
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'trustly'"
          />
          <AdyenCard
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'scheme'"
          />
          <ApplePay
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'applepay'"
          />
          <GooglePay
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'googlepay'"
          />
          <EPS
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'eps'"
              :banks="paymentMethod.issuers"
          />
          <AmazonPay
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod) === 'amazon_pay_v2'"
              :isButtonHidden="true"
          />
          <OnlineBanking
              v-if="activePaymentMethod === paymentMethod.service_type && getActivePaymentType(paymentMethod).startsWith('onlineBanking')"
              :banks="paymentMethod.issuers"
          />
        </li>
      </ul>
      <template #footer>
        <div @click="submit">
          <button type="submit" name="submit" @click.stop="submit" class="b b-A checkout-v2-btn">
            {{ $t('shopping.basket.checkout.purchase') }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
  import { useStore } from 'vuex'
  import { getAdyen, isAdyenValid as isAdyenValidRef, setFormSubmitPressed } from '@/composables/useAdyen'
  import { useRouter } from 'vue-router'
  import { resetAdyenErrors } from '@/composables/useAdyen'
  import { ref, inject, onMounted, onBeforeUnmount, nextTick, provide, watch } from 'vue'
  import { useCheckout } from '@/app/Checkout/composables/useCheckout'
  import { usePayment, paymentManager } from '@/composables/usePayment'
  import { useAuth } from '@/app/Checkout/composables/useAuth'

  import EPS from '@/components/payment/EPS/index.vue'
  import BaseModal from '@/components/BaseModal/index.vue'
  import Walley from '@/components/payment/Walley/index.vue'
  import Klarna from '@/components/payment/Klarna/index.vue'
  import PayPal from '@/components/payment/PayPal/index.vue'
  import Trustly from '@/components/payment/Trustly/index.vue'
  import ApplePay from '@/components/payment/ApplePay/index.vue'
  import GooglePay from '@/components/payment/GooglePay/index.vue'
  import AdyenCard from '@/components/payment/AdyenCard/index.vue'
  import AmazonPay from '@/components/payment/AmazonPay/index.vue'
  import PurchaseSummary from '@/components/PurchaseSummary/index.vue'
  import OnlineBanking from '@/components/payment/OnlineBanking/index.vue'
  import NBRadioGroup from '@/app/Checkout/components/common/NBRadioGroup/index.vue'
  import CartOverview from '@/app/Checkout/components/mobile/CartOverview/index.vue'
  import ShippingDetails from '@/app/Checkout/components/mobile/ShippingDetails/index.vue'
  import { useAddresses } from '@/app/Checkout/composables/useAddresses'
  import { trackScreenView, scrollToError } from '../../../../../helper'
  import { useI18n } from 'vue-i18n'
  import FreeSamplesBlock from '@/app/Checkout/components/common/FreeSamplesBlock/index.vue'
  import CartVoucherBlock from '@/app/Checkout/components/common/CartVoucherBlock/index.vue'

  const { t } = useI18n()
  const isOverlay = inject('isOverlay', false)
  const store = useStore()
  const router = useRouter()
  const container = ref(null)
  const isModalOpen = ref(false)
  const adyenInstance = getAdyen()
  const { checkout, getCheckout, shippingAddress, validateAddresses, activePaymentMethod } = useCheckout()
  const isAdyenValid = isAdyenValidRef
  const { getAdyenConfig, setPaymentMethod, availablePaymentMethods, proceedToPayment, getActivePaymentType, getAmazonPayConfig } =
    usePayment()
  const { isLoggedIn } = useAuth()
  const { errors, setErrors, isDeliveryAddressFilled, isBillingAddressSame } = useAddresses()

  const showDeliveryAddressRequiredError = ref(false)
  const showInvoiceAddressRequiredError = ref(false)

  provide('checkoutShowDeliveryAddressRequired', showDeliveryAddressRequiredError)
  provide('checkoutShowInvoiceAddressRequired', showInvoiceAddressRequiredError)

  const goBack = () => {
    window.history.back()
  }

  watch(
    () => isDeliveryAddressFilled('delivery_address'),
    (filled) => {
      if (filled) showDeliveryAddressRequiredError.value = false
    },
  )

  watch(
    () => [isBillingAddressSame.value, isDeliveryAddressFilled('invoice_address')],
    () => {
      if (isBillingAddressSame.value || isDeliveryAddressFilled('invoice_address')) {
        showInvoiceAddressRequiredError.value = false
      }
    },
  )

  const handlePaymentMethodChange = async ({ service_type }) => {
    await setPaymentMethod(service_type)

    if (service_type === 21) {
      await getAmazonPayConfig()
    } else {
      await getAdyenConfig()
    }
  }
  const setAddressValidationErrors = (errors) => {
    if (errors.delivery_address) {
      setErrors({ errors: { generic: errors.delivery_address }, type: 'delivery_address' })
    }
    if (errors.invoice_address) {
      setErrors({ errors: { generic: errors.invoice_address }, type: 'invoice_address' })
    }

    scrollToTop()
  }

  const validateAndProceedToPayment = async () => {
    try {
      const validated = await validateAddresses()

      if (validated?.errors) {
        throw validated.errors
      }

      await paymentManager.init()
    } catch (error) {
      setAddressValidationErrors(error.errors || {})
      isModalOpen.value = false
    }
  }

  const scrollToTop = () => {
    container.value?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const closeBtnRef = ref(null)
  let closeBtnHandler = null

  watch(isModalOpen, (val) => {
    if (!val) {
      resetAdyenErrors()
    }
  })

  const handlePageShow = event => {
    if (event.persisted) {
      isModalOpen.value = false
    }
  }

  onMounted(async () => {
    window.addEventListener('pageshow', handlePageShow)
    isModalOpen.value = false
    await getCheckout()

    // Vue @click doesn't fire (host overlay intercepts bubble phase). Use capture-phase listener.
    const el = closeBtnRef.value
    if (el) {
      closeBtnHandler = () => closeCart()
      el.addEventListener('click', closeBtnHandler, true)
    }
    trackScreenView('Checkout', {}, 'purchase')
  })

  onBeforeUnmount(() => {
    window.removeEventListener('pageshow', handlePageShow)
    const el = closeBtnRef.value
    if (el && closeBtnHandler) {
      el.removeEventListener('click', closeBtnHandler, true)
    }
  })

  const openPaymentOverlay = () => {
    showDeliveryAddressRequiredError.value = false
    showInvoiceAddressRequiredError.value = false

    const deliveryFilled = isDeliveryAddressFilled('delivery_address')
    const invoiceFilled = isDeliveryAddressFilled('invoice_address')
    const needsSeparateInvoice = !isBillingAddressSame.value

    // Missing address before is_invalid: empty rows often have is_invalid true.
    if (!deliveryFilled) {
      showDeliveryAddressRequiredError.value = true
    }
    if (needsSeparateInvoice && !invoiceFilled) {
      showInvoiceAddressRequiredError.value = true
    }
    if (!deliveryFilled || (needsSeparateInvoice && !invoiceFilled) || checkout.value?.delivery_address?.is_invalid) {
      return scrollToTop()
    } else if (shippingAddress.value?.is_invalid) {
      return scrollToTop()
    } else if (!isLoggedIn.value && !checkout.value?.delivery_address?.email) {
      setAddressValidationErrors({ delivery_address: t('messages.checkout.purchase.invalid_address') })
      return
    }

    isModalOpen.value = true
  }

  const submit = () => {
    setFormSubmitPressed(true)
    scrollToError()
    paymentManager.preInit()
    submitAfter()
  }

  const submitAfter = async () => {
    if (!checkout.value.delivery_address.is_invalid && checkout.value.is_delivery_country_valid) {
      await validateAndProceedToPayment()
    }
  }

  async function closeCart() {
    if (isOverlay) {
      await router.replace({ name: 'Cart' })
      await nextTick()
      document.dispatchEvent(new CustomEvent('quickcart:close', { bubbles: true }))
    } else {
      const match = window.location.pathname.match(/^\/([^/]+)\//)
      const locale = match ? match[1] : ''
      window.location.href = locale ? `/${locale}/` : '/'
    }
  }
</script>
